import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DroneIcon } from "@/components/DroneIcon";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const err = params.get("error");
    if (err) {
      toast.error(err, { duration: 8000 });
      params.delete("error");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      const res = await api.post("/auth/login", { email: email.trim(), password });
      if (res.data?.session_token) {
        localStorage.setItem("session_token", res.data.session_token);
      }
      setUser(res.data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Sign-in failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Sign-in failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left: auth panel */}
      <div className="flex flex-col justify-center px-8 sm:px-16 py-12 bg-white">
        <div className="max-w-md w-full mx-auto fade-up">
          <div className="flex items-center gap-3 mb-12">
            <div className="h-11 w-11 rounded-xl bg-slate-900 flex items-center justify-center">
              <DroneIcon className="h-6 w-6 text-lime-400" strokeWidth={2} />
            </div>
            <div>
              <p className="font-heading font-extrabold text-lg leading-none text-slate-900">Air Log</p>
              <p className="text-xs text-slate-500 tracking-wider uppercase">Aviation Management</p>
            </div>
          </div>

          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-3">Enhanced AI Performance</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 font-heading leading-[1.05]">
            Every job done right.<br />All on one platform.
          </h1>
          <p className="text-base leading-relaxed text-slate-600 mt-5">
            A simple yet powerful aviation management platform — from training and compliance to ops and accounts.
          </p>

          <Button
            data-testid="google-login-btn"
            onClick={handleLogin}
            className="mt-10 w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium text-base rounded-lg transition-all hover:-translate-y-0.5"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24"><path fill="#fff" d="M12.24 10.4v3.83h5.38c-.24 1.4-1.66 4.12-5.38 4.12-3.24 0-5.88-2.68-5.88-5.98s2.64-5.98 5.88-5.98c1.84 0 3.08.78 3.79 1.46l2.58-2.49C16.96 3.3 14.83 2.3 12.24 2.3 6.98 2.3 2.72 6.56 2.72 11.82s4.26 9.52 9.52 9.52c5.49 0 9.13-3.86 9.13-9.29 0-.62-.07-1.1-.16-1.58z"/></svg>
            Continue with Google
          </Button>
          <p className="text-xs text-slate-400 mt-4 text-center">Secure sign-in via Google. No password required.</p>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs uppercase tracking-wider text-slate-400">or sign in with email</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={handlePasswordLogin} className="space-y-3" data-testid="password-login-form">
            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              className="h-12"
              data-testid="login-email-input"
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="h-12"
              data-testid="login-password-input"
            />
            <Button
              type="submit"
              disabled={submitting}
              data-testid="password-login-btn"
              className="w-full h-12 bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold text-base rounded-lg transition-all hover:-translate-y-0.5 disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1508175688576-0c076b47b5b5"
          alt="Drone over agricultural fields"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
      </div>
    </div>
  );
}
